import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { GET_ALL_LIST_CONTEST_BY_LIMIT } from '../shared/HomeQueryList';
import FeatureContentCard from '../shared/FeatureContentCard';
import FeatureVideoCard from '../shared/FeatureVideoCard';
import QuizCardNew from '../shared/QuizCardNew';
import ListCard from '../shared/ListCard';
import GET_LIST_BY_ID from '../../gql/queries/get-list-by-id.gql';
import Spinner from '../shared/spinner';
import NoDataFound from '../shared/NoDataFound';

export default function ListPage({ listId }) {
  const router = useRouter();
  const [apiLimit] = useState(12);
  const [apiOffset, setApiOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [list, setList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [type, setType] = useState('');
  const [activeListId, setActiveListId] = useState(null);
  const [isSublistParent, setIsSublistParent] = useState(false);

  const initialLoadDone = useRef(false);
  const isTabSwitching = useRef(false);
  const isSublistParentLocked = useRef(false);

  const {
    data,
    fetchMore,
    refetch,
    loading: listLoading,
    error: listError,
  } = useQuery(GET_ALL_LIST_CONTEST_BY_LIMIT, {
    variables: {
      listId: activeListId || listId,
      limit: apiLimit,
      offset: apiOffset,
      isHome: false,
    },
    notifyOnNetworkStatusChange: true,
    skip: !listId,
  });

  const {
    data: listData,
    loading: listDataLoading,
    error: listDataError,
  } = useQuery(GET_LIST_BY_ID, {
    variables: {
      listId,
    },
    notifyOnNetworkStatusChange: true,
    skip: !listId || data?.getListContents?.list.type !== 'list',
  });

  const handleScroll = () => {
    const scrollPosition = window.scrollY + window.innerHeight;
    const { scrollHeight } = document.documentElement;

    if (scrollPosition < scrollHeight - 700 || fetching || !hasMore) return;

    setFetching(true);

    const nextOffset = apiOffset + apiLimit;

    const currentListId = activeListId || listId;

    fetchMore({
      variables: {
        listId: currentListId,
        limit: apiLimit,
        offset: nextOffset,
        isHome: false,
      },
    }).finally(() => {
      setApiOffset(nextOffset);
      setFetching(false);
    });
  };

  // Reset state when listId changes
  useEffect(() => {
    setItems([]);
    setList([]);
    setApiOffset(0);
    setHasMore(true);
    setActiveListId(null);
    setIsSublistParent(false);
    initialLoadDone.current = false;
    isTabSwitching.current = false;
    isSublistParentLocked.current = false;
  }, [listId]);

  useEffect(() => {
    if (data?.getListContents?.contents) {
      setItems((prevItems) => [...prevItems, ...data.getListContents.contents]);
      if (data.getListContents.contents.length < apiLimit) {
        setHasMore(false);
      }
      initialLoadDone.current = true;
      isTabSwitching.current = false;
    }
  }, [data, apiLimit]);

  useEffect(() => {
    if (listData?.getLists) {
      setList((prevItems) => [...prevItems, ...listData.getLists]);
    }
  }, [listData]);

  useEffect(() => {
    if (data?.getListContents?.list?.type) {
      setType(data.getListContents.list.type);
    }
  }, [data]);

  useEffect(() => {
    if (!isSublistParentLocked.current && data?.getListContents?.list) {
      const value = !!data.getListContents.list.isSublist;
      setIsSublistParent(value);
      if (value) {
        isSublistParentLocked.current = true;
      }
    }
  }, [data]);

  useEffect(() => {
    if (
      isSublistParent
      && list?.length > 0
      && !activeListId
    ) {
      const defaultSublist = list[0];
      if (defaultSublist?.id) {
        setActiveListId(defaultSublist.id);
        setItems([]);
        setApiOffset(0);
        setHasMore(true);
        isTabSwitching.current = true;
        refetch({
          listId: defaultSublist.id,
          limit: apiLimit,
          offset: 0,
          isHome: false,
        });
      }
    }
  }, [isSublistParent, list, activeListId, refetch, apiLimit]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fetching, hasMore]);

  const handleTabClick = (tabId) => {
    if (!tabId || tabId === activeListId) return;
    setActiveListId(tabId);
    setItems([]);
    setApiOffset(0);
    setHasMore(true);
    isTabSwitching.current = true;
    refetch({
      listId: tabId,
      limit: apiLimit,
      offset: 0,
      isHome: false,
    });
  };

  const showFullPageSpinner = (listLoading || listDataLoading) && !initialLoadDone.current;
  const showContentSpinner = listLoading && isTabSwitching.current;

  if (showFullPageSpinner) {
    return <Spinner fullPage />;
  }

  if (listError || listDataError) {
    return <NoDataFound noMsg="Something went wrong." />;
  }

  if (items.length === 0 && list.length === 0 && !listLoading && !isSublistParent && !listDataLoading) {
    return <NoDataFound noMsg="No Data found." />;
  }

  return (
    <div className="mx-4 md:mx-20 my-3 min-h-[70vh]">
      {isSublistParent && list?.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {list.map((sublist) => (
              <button
                type="button"
                key={sublist?.id}
                onClick={() => handleTabClick(sublist?.id)}
                className={`px-4 py-2 text-sm md:text-base whitespace-nowrap rounded-full border-2 font-medium transition-all duration-200 ${
                  activeListId === sublist?.id
                    ? 'border-tabBlue bg-tabBlue text-white shadow-md'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-tabBlue hover:text-tabBlue hover:bg-blue-50'
                }`}
              >
                {sublist?.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {data?.getListContents?.list?.htmlDescription && (
      <div dangerouslySetInnerHTML={{ __html: data?.getListContents?.list?.htmlDescription }} className="mb-4" />
      )}

      <div className="relative min-h-[200px]">
        {showContentSpinner ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <>
            {
              type === 'quiz'
              && (
                <QuizCardNew
                  sectionTitle={data?.getListContents?.list?.name}
                  page={data?.getListContents?.list?.name}
                  data={items}
                  entityID={data?.getListContents?.list?.id}
                  pageType="view-all"
                />
              )
            }
            <div className={`${type === 'video' ? 'grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4' : 'grid md:grid-cols-2 grid-cols-1 gap-2'}`}>
              {
                items?.map((content) => {
                  if (type === 'video') {
                    return <FeatureVideoCard video={content} key={content?.id} />;
                  }
                  if (type === 'audio' || type === 'pdf') {
                    return (
                      <FeatureContentCard
                        content={content}
                        type={data?.getListContents?.list?.type}
                        key={content?.id}
                        handleClick={({ id }) => router.push(`${router.asPath}/${id}`)}
                      />
                    );
                  }
                  return null;
                })
              }
              {
                type === 'list' && !isSublistParent && list?.map((content) => (<ListCard content={content} key={content?.id} isslugPage />))
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
}

ListPage.propTypes = {
  listId: PropTypes.string.isRequired,
};
